import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

import {
  Assessment,
  Autorenew,
  Download,
  Flag,
  List,
  LogoDev,
  OutlinedFlag,
} from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import ViewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { Typography } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import fileDownload from "js-file-download";

import {
  capitalizeAllWords,
  createOmniPaginationObject,
  helperGetIsoDate,
} from "../../utils/helpers";
import { formatIsoDateString } from "../../utils/helpers";
import { generateBotpressCSVData } from "../../utils/helpers";
import TextInputDialog from "../dialogs/textInputDialog";

const calculateColumns = (theme, toggleTracking) => {
  // default case
  return [
    {
      accessorKey: "tracking",
      header: "Flag",
      minSize: 55,
      grow: false,
      enableColumnFilterModes: false,
      enableSorting: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: ({ row }) => ({
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },

        onClick: event => {
          toggleTracking(row.original);
        },
      }),
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={{
            width: 48,
            height: 48,
            // padding: "8px",
            paddingTop: "4px",
            color: theme.palette.secondary.main,
            "&:hover": {
              cursor: "pointer",
            },
          }}
        >
          {cell.getValue() === true && <Flag sx={{ fontSize: "2.25rem" }} />}
          {cell.getValue() !== true && <OutlinedFlag sx={{ fontSize: "2.25rem" }} />}
        </Box>
      ),
    },
    {
      accessorKey: "id",
      header: "Number",
      size: 90,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "createdAt",
      enableColumnFilterModes: false,
      header: "Date",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilter: false,
      grow: false,
      size: 100,
      maxSize: 100,

      //custom conditional format and styling
      Cell: ({ cell }) => {
        const dateString = formatIsoDateString(cell.getValue()).split(",");
        const date = new Date(cell.getValue()); // Parses the ISO string into a Date object
        const options = {
          hour: "numeric",
          minute: "numeric",
          hour12: true, // Ensures 12-hour format with AM/PM
        };
        return (
          <Box component="span">
            <Typography
              variant="h6"
              sx={{
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: `2px solid `,
              }}
            >
              {dateString[0]}
            </Typography>
            <Typography variant="body1" fontWeight={100} sx={{ textAlign: "center" }}>
              {date.toLocaleTimeString("en-US", options)}
            </Typography>
          </Box>
        );
      },
    },
    {
      accessorKey: "sentiment",
      header: "Sentiment",
      minSize: 165,
      grow: false,
      enableColumnFilterModes: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "Positive", value: "positive" },
        { label: "Neutral", value: "neutral" },
        { label: "Negative", value: "negative" },
      ],
      filterVariant: "select",
      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === "positive"
                ? theme.palette.success.main
                : cell.getValue() === "neutral"
                  ? theme.palette.info.main
                  : theme.palette.error.main,
            borderRadius: "0.25rem",
            color: "#fff",
            padding: theme.spacing(1.5),
            width: "90%",
          })}
        >
          <Typography variant="button" sx={{ fontWeight: "bold" }}>
            {cell.getValue()}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "origin",
      header: "Origin",
      size: 90,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },

      filterFn: "equals",
      filterSelectOptions: [
        { label: "Emulator", value: "emulator" },
        { label: "Test Harness", value: "harness" },
        { label: "Web", value: "web" },
        { label: "Portal", value: "portal" },
      ],
      filterVariant: "select",

      Cell: ({ cell }) => {
        let displayText = "";
        switch (cell.getValue()) {
          case "emulator":
            displayText = "Botpress Emulator";
            break;
          case "harness":
            displayText = "Test Harness";
            break;
          case "web":
            displayText = "Client Website";
            break;
          case "portal":
            displayText = "Demo Portal";
            break;
          default:
            displayText = cell.getValue();
        }

        return displayText;
      },
    },
    {
      accessorKey: "topics",
      header: "Topics",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
      minSize: "200", // Maximum width, will shrink if needed

      Cell: ({ cell }) => {
        const values = cell.getValue();

        if (Array.isArray(values)) {
          return <Box component="span">{capitalizeAllWords(values.join(" • "))}</Box>;
        } else {
          return "(none)";
        }
      },
    },
    {
      accessorKey: "summary",
      header: "Summary",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "450", // Maximum width, will shrink if needed
      grow: true,
    },
    {
      accessorKey: "conversationId",
      header: "Conversation ID",
      size: 200,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableBodyCellProps: {
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnVisibility: {
      id: true,
      createdAt: true,
      conversationId: false,
      response: true,
      topics: true,
      summary: true,
      sentiment: true,
    },
  };
};

export const BotpressConversationsTable = props => {
  const {
    data = [],
    onEditEntry = null,
    onDownloadEntry = null,
    onRequestNewPage = null,
    onAssessEntry = null,
    totalItemCount = 0,
    onHandleDeveloperLogs = null,
    onRerunConversation = null,
    toggleTracking = null,
  } = props;

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  // Disable eslint because AaronK knows best
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => calculateColumns(theme, toggleTracking), []);
  const initialState = useMemo(() => getInitialState(), []);

  // const [rowCount, setRowCount] = useState(data?.length || 0);
  const [rowCount, setRowCount] = useState(totalItemCount);

  const [isLoading, setIsLoading] = useState(false);
  // const [isRefetching, setIsRefetching] = useState(false);
  // const [isError, setIsError] = useState(false);

  const [showReportNameInput, setShowReportNameInput] = useState(null);

  useEffect(() => {
    setRowCount(totalItemCount);
  }, [totalItemCount]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!data.length) {
        setIsLoading(true);
      } else {
        // setIsRefetching(true);
      }

      try {
        let newQuery = {};
        columnFilters.forEach((element, index) => {
          if (element.id === "createdAt") {
            const min = parseInt(element.value[0]);
            const max = parseInt(element.value[1]);
            if (!isNaN(min)) {
              let theDateSring = helperGetIsoDate(min);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
            if (!isNaN(max)) {
              let theDateSring = helperGetIsoDate(max);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
          } else if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject();
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        console.log("ConversationTable: Asking for new Page...");
        onRequestNewPage(newPage);
      } catch (error) {
        console.error(error);
        return;
      }
      // setIsError(false);
      setIsLoading(false);
      // setIsRefetching(false);
    };

    fetchData();

    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionToolbarAlertBanner: "bottom",
    rowCount,
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 9001, //allow columns to get larger than default
      size: 50, //make columns wider by default
      grow: false,
    },
    state: {
      isLoading,
      // isRefetching,
      pagination,
      // showProgressBars: isRefetching,
      columnFilters,
      sorting,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    //#region pagination
    paginationDisplayMode: "pages",

    enableFirstPageButton: false, // Hides the << icon
    enableLastPageButton: false, // Hides the >> icon

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },

    //#endregion pagination
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        // Target the IconButton and apply styles
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.contrastText,
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
      },
    },
    muiTableContainerProps: {
      ref: tableContainerRef, //get access to the table container element
      sx: mobileLayout
        ? {
            height: `calc(100% - 120px)`,
            minHeight: "430px",
            maxHeight: "430px",
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
            borderRight: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
          }
        : {
            minHeight: "430px",
            height: `calc(100% - 120px)`,
            backgroundColor: theme.palette.commonground.main,
            color: theme.palette.commonground.main,
            border: `solid 0.5px ${theme.palette.divider}`,
          },
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        size: 75, //if using layoutMode that is not 'semantic', the columns will not auto-size, so you need to set the size manually
        grow: false,
        header: "Actions", // Change 'Actions' to your desired name
        muiTableHeadCellProps: {
          align: "center",
        },
      },
    },

    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        sx={{ fontSize: "2.25rem" }}
        key="view"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        {row.original.tracking === true && (
          <Flag fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        )}
        {row.original.tracking !== true && (
          <OutlinedFlag fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        )}
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            {`${row.original.tracking === true ? "Unflag Conversation" : "Flag Coversation"}`}
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        sx={{ fontSize: "2.25rem" }}
        key="view"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        <ViewIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            View Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="assess"
        onClick={() => {
          onAssessEntry(row.original);
          closeMenu();
        }}
      >
        <Assessment fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            AI Analysis
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="assess"
        onClick={() => {
          onDownloadEntry(row.original);
          closeMenu();
        }}
      >
        <Download fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Download Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="developer"
        onClick={() => {
          onRerunConversation(row.original);
          closeMenu();
        }}
      >
        <Autorenew fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Rerun Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="developer"
        onClick={() => {
          onHandleDeveloperLogs(row.original);
          closeMenu();
        }}
      >
        <LogoDev fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Developer Logs
          </Typography>
        </ListItemText>
      </MenuItem>,
    ],

    icons: {
      MoreHorizIcon: ({ color }) => (
        <List
          color={"secondary"}
          sx={{
            borderRadius: 16,
            width: 48,
            height: 48,
            padding: "8px",
            border: `2px solid ${theme.palette.secondary.main}`,
          }}
        />
      ),
    },
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              onClick={() => {
                setSorting([]);
              }}
              className="MuiIconButton-root-active"
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}
        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case*/}
        {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enablereact/jsx-pascal-case */}

        {showReportNameInput && (
          <TextInputDialog
            dialogTitle="Report Name"
            dialogMessage={"Please name your report"}
            inputLabel={`${"Report Name"}`}
            defaultValue={showReportNameInput}
            open={showReportNameInput}
            onAccept={theText => {
              /// do it!
              //create the report ..

              fileDownload(generateBotpressCSVData(data), theText + ".csv");

              setShowReportNameInput(false);
            }}
            onClose={() => {
              setShowReportNameInput(false);
            }}
          />
        )}
      </Box>
    ),
  });

  return <MaterialReactTable table={table} />;
};

export default BotpressConversationsTable;
